import { WifiOff, Wifi, RefreshCw, CloudOff, Check, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSyncState } from "@/hooks/useSyncState";
import { cn } from "@/lib/utils";

export function OfflineIndicator() {
  const { language } = useLanguage();
  const { isOnline, status, pendingCount, lastSync, sync } = useSyncState();

  // Don't show if online and no pending items and not syncing
  if (isOnline && pendingCount === 0 && status !== 'syncing') {
    return null;
  }

  const getStatusIcon = () => {
    if (!isOnline) return <WifiOff className="w-4 h-4" />;
    if (status === 'syncing') return <RefreshCw className="w-4 h-4 animate-spin" />;
    if (status === 'success') return <Check className="w-4 h-4" />;
    if (status === 'error') return <AlertCircle className="w-4 h-4" />;
    if (pendingCount > 0) return <CloudOff className="w-4 h-4" />;
    return <Wifi className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (!isOnline) {
      return language === "es" ? "Sin conexión" : "Offline";
    }
    if (status === 'syncing') {
      return language === "es" ? "Sincronizando..." : "Syncing...";
    }
    if (pendingCount > 0) {
      return language === "es"
        ? `${pendingCount} pendiente${pendingCount > 1 ? 's' : ''}`
        : `${pendingCount} pending`;
    }
    return language === "es" ? "En línea" : "Online";
  };

  const getVariant = (): "default" | "secondary" | "destructive" | "outline" => {
    if (!isOnline) return "destructive";
    if (status === 'error') return "destructive";
    if (status === 'syncing') return "secondary";
    if (pendingCount > 0) return "outline";
    return "secondary";
  };

  const handleManualSync = () => {
    if (isOnline && pendingCount > 0) {
      sync();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2 fade-in duration-300">
      <div className="flex items-center gap-2">
        {/* Manual sync button when there are pending items */}
        {isOnline && pendingCount > 0 && status !== 'syncing' && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleManualSync}
            className="h-8 px-3 shadow-lg backdrop-blur-sm bg-background/80"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            {language === "es" ? "Sincronizar" : "Sync"}
          </Button>
        )}

        {/* Status badge */}
        <Badge
          variant={getVariant()}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 shadow-lg backdrop-blur-sm",
            !isOnline && "animate-pulse"
          )}
        >
          {getStatusIcon()}
          <span className="font-medium text-sm">{getStatusText()}</span>
        </Badge>
      </div>

      {/* Last sync time (subtle) */}
      {lastSync && isOnline && pendingCount === 0 && (
        <p className="text-[10px] text-muted-foreground text-right mt-1 pr-1">
          {language === "es" ? "Última sync: " : "Last sync: "}
          {new Date(lastSync).toLocaleTimeString(language === "es" ? "es-ES" : "en-US", {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      )}
    </div>
  );
}
